import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { MessageSquareText, TrendingUp, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { timeAgo } from '../utils/relativeTime';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Surfaces the most recent match evaluation (rating + comment + strengths + improvements)
 * for the current Player/Parent on the dashboard, so they don't have to drill into
 * My Performance to read their coach's notes.
 *
 * No new endpoints — calls existing GET /api/athletes (own/linked) + GET /api/performance/athlete/{id}.
 */
const LatestCoachFeedback = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState(null);
  const [athleteName, setAthleteName] = useState('');

  useEffect(() => {
    let alive = true;
    const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    (async () => {
      try {
        const athletes = (await axios.get(`${API}/api/athletes`, h)).data || [];
        if (!athletes.length) return;
        const athlete = athletes[0];
        if (alive) setAthleteName(athlete.full_name || '');
        const perfs = (await axios.get(`${API}/api/performance/athlete/${athlete.id}`, h)).data || [];
        if (!perfs.length) return;
        // Most recent by created_at or the first entry (list is usually desc).
        const sorted = [...perfs].sort((a, b) => {
          const ad = new Date(a.created_at || 0).getTime();
          const bd = new Date(b.created_at || 0).getTime();
          return bd - ad;
        });
        if (alive) setLatest(sorted[0]);
      } catch {
        /* swallow — card renders empty state */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const hasFeedback =
    latest && (latest.coach_comment || latest.strengths || latest.improvements || latest.rating);

  const ratingCls = (r) => {
    if (!r) return 'bg-gray-100 text-gray-600';
    if (r >= 8) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
    if (r >= 6) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
    if (r >= 4) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  };

  return (
    <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="latest-coach-feedback">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <MessageSquareText size={16} className="text-purple-500" />
            Latest Coach Feedback
          </CardTitle>
          <button
            onClick={() => navigate('/my-performance')}
            className="text-xs text-pfap-green hover:underline flex items-center gap-0.5"
            data-testid="latest-coach-feedback-open"
          >
            View All <ChevronRight size={14} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <p className="text-xs text-gray-400 text-center py-6">Loading…</p>
        ) : !hasFeedback ? (
          <p className="text-xs text-gray-400 text-center py-6" data-testid="latest-coach-feedback-empty">
            No coach feedback yet — it will appear here after your next match.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {athleteName ? `${athleteName} · ` : ''}
                <span data-testid="latest-coach-feedback-time">{timeAgo(latest.created_at)}</span>
              </p>
              {latest.rating ? (
                <Badge className={`${ratingCls(latest.rating)} text-xs font-medium`} data-testid="latest-coach-feedback-rating">
                  <TrendingUp size={10} className="mr-1" /> {latest.rating}/10
                </Badge>
              ) : null}
            </div>
            {latest.coach_comment ? (
              <p className="text-sm text-gray-700 dark:text-gray-300 italic" data-testid="latest-coach-feedback-comment">
                &ldquo;{latest.coach_comment}&rdquo;
              </p>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {latest.strengths ? (
                <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/40 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-1">
                    Strengths
                  </p>
                  <p className="text-xs text-emerald-900 dark:text-emerald-100" data-testid="latest-coach-feedback-strengths">
                    {latest.strengths}
                  </p>
                </div>
              ) : null}
              {latest.improvements ? (
                <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/40 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-blue-700 dark:text-blue-300 mb-1">
                    Focus on
                  </p>
                  <p className="text-xs text-blue-900 dark:text-blue-100" data-testid="latest-coach-feedback-improvements">
                    {latest.improvements}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LatestCoachFeedback;
